import { useCallback, useContext } from 'react';
import { useFunctionInit } from '@laigu/store';
import isPromise from './is-promise';

import useUpgradeContext from '../../hooks/use-upgrade-context';

const { UpgradeContext } = useUpgradeContext;

function useUpgrade() {
  const { onOpen, setCurrentPackageKey, onPermOpen } = useContext(UpgradeContext);
  const { functionMap = {}, permReady, permMap } = useFunctionInit();

  // isPage 是否是全页面显示
  const checkPackage = useCallback(
    (packageKey = '', showTip = true) => {
      if (!packageKey) {
        return true;
      }
      // 检测是否有功能包
      const hasFunction = permReady ? !!functionMap[packageKey] : false;
      if (!hasFunction && showTip) {
        setCurrentPackageKey(packageKey);
        onOpen();
      }
      return hasFunction;
    },
    [permReady, functionMap]
  );

  const bindPackage = useCallback(
    (callback, packageKey) => {
      return (...args) => {
        if (packageKey && !checkPackage(packageKey)) {
          return;
        }
        const result = callback.apply(this, args);
        if (isPromise(result)) {
          return result;
        }
      };
    },
    [permReady, functionMap, checkPackage]
  );

  const checkPerm = useCallback(
    (permKey = '', showTip = true) => {
      if (!permKey) {
        return true;
      }
      // 检测是否有权限
      const hasPerm = permReady ? !!permMap[permKey] : false;
      if (!hasPerm && showTip) {
        onPermOpen();
      }
      return hasPerm;
    },
    [permReady, permMap]
  );

  const bindPerm = useCallback(
    (callback, permKey) => {
      return (...args) => {
        if (permKey && !checkPerm(permKey)) {
          return;
        }
        const result = callback.apply(this, args);
        if (isPromise(result)) {
          return result;
        }
      };
    },
    [permReady, permMap, checkPerm]
  );

  const bindFunction = useCallback(
    (callback, functionKey) => {
      return (...args) => {
        if (functionKey && !checkPackage(functionKey)) {
          return;
        }
        const result = callback.apply(this, args);
        if (isPromise(result)) {
          return result;
        }
      };
    },
    [checkPackage, checkPerm, permReady, permMap, functionMap]
  );

  const bindFunctionAndPerm = useCallback(
    (callback, functionKey, permKey) => {
      return (...args) => {
        if (functionKey && !checkPackage(functionKey)) {
          return;
        }
        if (permKey && !checkPerm(permKey)) {
          return;
        }
        const result = callback.apply(this, args);
        if (isPromise(result)) {
          return result;
        }
      };
    },
    [checkPackage, checkPerm, permReady, permMap, functionMap]
  );

  // functionFirst 参数用于忽略功能包判断,在没有功能包的情况下直接返回true，在有功能包的情况下再判断是否有权限
  const checkFunctionAndPerm = useCallback(
    (functionKey, permKey, showTip = true, functionFirst = false) => {
      if (functionKey && !checkPackage(functionKey, showTip)) {
        return !!functionFirst;
      }
      return !(permKey && !checkPerm(permKey, showTip));
    },
    [checkPackage, checkPerm, permReady, permMap, functionMap]
  );

  return {
    checkPackage,
    bindPackage,
    checkPerm,
    bindPerm,
    bindFunction,
    bindFunctionAndPerm,
    checkFunctionAndPerm
  };
}

export default useUpgrade;
